import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { formatCurrency } from "../../../utils/formatCurrency";
import {
  CacBreakdown,
  CATEGORY_OPTIONS,
  PeriodPreset,
  SEGMENT_LABELS,
  UnitEconomicsData,
  UnitEconomicsSegment,
} from "../../../types/unit-economics.types";
import {
  useUnitEconomicsQuery,
  useUnitEconomicsCohortsQuery,
} from "../../../hooks/useUnitEconomics";
import {
  computePresetRange,
  computePreviousRange,
  formatRangeLabel,
} from "./periodHelpers";
import PeriodSelector from "./PeriodSelector";
import HeroMetricCard from "./HeroMetricCard";
import MetricsTable, { MetricGroup } from "./MetricsTable";
import CohortChart from "./CohortChart";
import AcquisitionCostsSection from "./AcquisitionCostsSection";
import { formatMrrTooltipLabel } from "./mrrTooltipLabel";

const DEFAULT_PRESET: PeriodPreset = "last-28-days";

type TabKey = "metrics" | "cohorts";

const TABS: { key: TabKey; label: string }[] = [
  { key: "metrics", label: "Wskaźniki" },
  { key: "cohorts", label: "Kohorty" },
];

const formatCount = (n: number): string => n.toLocaleString("pl-PL");
const formatCents = (cents: number): string => formatCurrency(cents / 100);
const formatPct = (v: number): string =>
  `${v.toLocaleString("pl-PL", { maximumFractionDigits: 1 })} %`;
const formatRatio = (v: number): string =>
  `${v.toLocaleString("pl-PL", { maximumFractionDigits: 1 })}×`;
const formatMonths = (v: number): string =>
  `${v.toLocaleString("pl-PL", { maximumFractionDigits: 1 })} mies.`;
const formatDateShort = (iso: string): string =>
  new Date(iso).toLocaleDateString("pl-PL");

const categoryLabel = (category: string): string =>
  CATEGORY_OPTIONS.find((c) => c.value === category)?.label ?? category;

// ---------------------------------------------------------------------------
// Small status primitives — reused by every section's independent state (C3)
// ---------------------------------------------------------------------------

const LoadingSpinner: React.FC<{ label?: string }> = ({
  label = "Ładowanie",
}) => (
  <div className="flex justify-center py-12">
    <div
      role="status"
      aria-label={label}
      className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600"
    />
  </div>
);

const ErrorBanner: React.FC<{ message: string }> = ({ message }) => (
  <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-4">
    <p className="text-red-600">{message}</p>
  </div>
);

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <p className="rounded-lg border border-neutral-200 bg-white p-8 text-center text-sm text-neutral-500">
    {message}
  </p>
);

// ---------------------------------------------------------------------------
// UnitEconomicsTab — period selector + URL-based [Wskaźniki][Kohorty] tabs
// ---------------------------------------------------------------------------

interface UnitEconomicsTabProps {
  /** Whether the current user can perform write actions (admin only). */
  canWrite: boolean;
}

const UnitEconomicsTab: React.FC<UnitEconomicsTabProps> = ({ canWrite }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab: TabKey =
    searchParams.get("tab") === "cohorts" ? "cohorts" : "metrics";

  const setActiveTab = (tab: TabKey) => {
    const next = new URLSearchParams(searchParams);
    if (tab === "metrics") {
      next.delete("tab");
    } else {
      next.set("tab", tab);
    }
    setSearchParams(next, { replace: true });
  };

  // Period state is encoded in the URL so deep-links / back-button restore it.
  const preset = (searchParams.get("preset") as PeriodPreset) || DEFAULT_PRESET;
  const customFrom = searchParams.get("from") ?? "";
  const customTo = searchParams.get("to") ?? "";

  const updateParams = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(changes)) {
      if (v === null || v === "") next.delete(k);
      else next.set(k, v);
    }
    setSearchParams(next, { replace: true });
  };

  // Resolve the active range: presets compute from now; custom uses URL inputs.
  const range = useMemo(() => {
    if (preset === "custom") {
      return { from: customFrom, to: customTo };
    }
    return computePresetRange(preset);
  }, [preset, customFrom, customTo]);

  // Validation: from strictly before to (half-open [from, to) → equal = empty).
  const rangeError = useMemo<string | null>(() => {
    if (preset !== "custom") return null;
    if (!range.from || !range.to) return null;
    if (range.from >= range.to) {
      return "Data „od” musi być wcześniejsza niż data „do”.";
    }
    return null;
  }, [preset, range.from, range.to]);

  const handlePresetChange = (next: PeriodPreset) => {
    if (next === "custom") {
      const r = computePresetRange(preset === "custom" ? DEFAULT_PRESET : preset);
      updateParams({ preset: "custom", from: r.from, to: r.to });
    } else {
      updateParams({ preset: next, from: null, to: null });
    }
  };

  const currentPeriodLabel = useMemo<string | undefined>(() => {
    if (rangeError || !range.from || !range.to) return undefined;
    return formatRangeLabel(range);
  }, [rangeError, range]);

  const comparisonPeriodLabel = useMemo<string | undefined>(() => {
    if (rangeError) return undefined;
    const prev = computePreviousRange(range);
    return prev ? formatRangeLabel(prev) : undefined;
  }, [rangeError, range]);

  return (
    <div className="space-y-6">
      <PeriodSelector
        preset={preset}
        customFrom={customFrom || range.from}
        customTo={customTo || range.to}
        rangeError={rangeError}
        onPresetChange={handlePresetChange}
        onCustomFromChange={(v) => updateParams({ preset: "custom", from: v })}
        onCustomToChange={(v) => updateParams({ preset: "custom", to: v })}
      />

      {currentPeriodLabel && (
        <div className="text-sm text-neutral-600">
          <span className="font-medium text-neutral-800">Okres:</span>{" "}
          {currentPeriodLabel}
          {comparisonPeriodLabel && (
            <>
              {" · "}
              <span className="font-medium text-neutral-800">
                Poprzedni okres:
              </span>{" "}
              {comparisonPeriodLabel}
            </>
          )}
        </div>
      )}

      {/* Tabs (URL-based, aria-current per spec C3) */}
      <div
        role="tablist"
        aria-label="Widoki unit economics"
        className="border-b border-neutral-200"
      >
        <nav className="-mb-px flex space-x-8">
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-current={isActive ? "page" : undefined}
                onClick={() => setActiveTab(tab.key)}
                className={`border-b-2 px-1 py-2 text-sm font-medium ${
                  isActive
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-neutral-500 hover:text-neutral-700"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === "metrics" && (
        <MetricsView
          range={range}
          rangeError={rangeError}
          comparisonPeriodLabel={comparisonPeriodLabel}
        />
      )}

      {activeTab === "cohorts" && (
        <CohortsView range={range} rangeError={rangeError} />
      )}

      <AcquisitionCostsSection canWrite={canWrite} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// MetricsView — Hero-strip + grouped MetricsTable, independent state (C3)
// ---------------------------------------------------------------------------

const MetricsView: React.FC<{
  range: { from: string; to: string };
  rangeError: string | null;
  comparisonPeriodLabel?: string;
}> = ({ range, rangeError, comparisonPeriodLabel }) => {
  const queryRange = rangeError ? { from: "", to: "" } : range;
  const { data, isLoading, error } = useUnitEconomicsQuery(queryRange);

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorBanner message="Błąd ładowania danych" />;
  if (!data)
    return (
      <EmptyState message="Brak wystarczających danych dla wybranego okresu." />
    );

  return (
    <div className="space-y-6">
      <HeroStrip data={data} comparisonPeriodLabel={comparisonPeriodLabel} />
      <MetricsTable
        groups={buildMetricGroups(data)}
        comparisonPeriodLabel={comparisonPeriodLabel}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// HeroStrip — 4 headline KPIs: MRR · NRR · LTV:CAC · Payback
// ---------------------------------------------------------------------------

export const HeroStrip: React.FC<{
  data: UnitEconomicsData;
  comparisonPeriodLabel?: string;
}> = ({ data, comparisonPeriodLabel }) => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
    <HeroMetricCard
      label="MRR"
      value={data.mrrCents.current}
      prevValue={data.mrrCents.previous}
      formatFn={formatCents}
      trend={data.trend.mrrCents}
      comparisonPeriodLabel={comparisonPeriodLabel}
      infoBadgeTitle={MRR_COVERAGE_BADGE_TITLE}
    />
    <HeroMetricCard
      label="NRR"
      subtitle="1 − churn przychodu brutto"
      value={data.nrrPct.current}
      prevValue={data.nrrPct.previous}
      formatFn={formatPct}
      trend={data.trend.nrrPct}
      comparisonPeriodLabel={comparisonPeriodLabel}
    />
    <HeroMetricCard
      label="LTV:CAC"
      value={data.ltvCacRatio.current}
      prevValue={data.ltvCacRatio.previous}
      formatFn={formatRatio}
      trend={data.trend.ltvCacRatio}
      comparisonPeriodLabel={comparisonPeriodLabel}
    />
    <HeroMetricCard
      label="Payback"
      subtitle="mniej = lepiej"
      value={data.paybackMonths.current}
      prevValue={data.paybackMonths.previous}
      formatFn={formatMonths}
      trend={data.trend.paybackMonths}
      comparisonPeriodLabel={comparisonPeriodLabel}
      invertDelta
    />
  </div>
);

// ---------------------------------------------------------------------------
// CohortsView — line chart on a separate endpoint, independent state (C3)
// ---------------------------------------------------------------------------

const CohortsView: React.FC<{
  range: { from: string; to: string };
  rangeError: string | null;
}> = ({ range, rangeError }) => {
  const queryRange = rangeError ? { from: "", to: "" } : range;
  const { data, isLoading, error } = useUnitEconomicsCohortsQuery(queryRange);

  if (isLoading) return <LoadingSpinner label="Ładowanie kohort" />;
  if (error) return <ErrorBanner message="Błąd ładowania danych kohortowych" />;
  if (!data || data.length === 0)
    return <EmptyState message="Brak wystarczających danych kohortowych." />;

  return <CohortChart cohorts={data} formatFn={formatCents} />;
};

// ---------------------------------------------------------------------------
// Drill-down helpers (shared TenantList + CAC breakdown content)
// ---------------------------------------------------------------------------

/** A compact "tenant → value" list used inside a drill-down popover. */
const TenantList: React.FC<{
  rows: { id: string; name: string; right: string }[];
}> = ({ rows }) => {
  if (rows.length === 0) {
    return <p className="text-xs text-neutral-500">Brak danych w okresie</p>;
  }
  return (
    <ul className="divide-y divide-neutral-100">
      {rows.map((r) => (
        <li
          key={r.id}
          className="flex items-start justify-between gap-3 py-1 text-xs"
        >
          <span className="min-w-0 flex-1 truncate text-neutral-700">
            {r.name}
          </span>
          <span className="max-w-[45%] shrink-0 whitespace-normal break-words text-right font-mono text-neutral-500">
            {r.right}
          </span>
        </li>
      ))}
    </ul>
  );
};

/** CAC drill-down: the division plus the new-paying list and the cost list. */
const CacBreakdownContent: React.FC<{ cac: CacBreakdown }> = ({ cac }) => (
  <div className="space-y-3 text-xs">
    <div className="rounded bg-neutral-50 p-2 text-neutral-700">
      {cac.payingCount === 0 ? (
        <>
          CAC nieobliczalny — brak nowych klientów płacących w okresie (koszty:{" "}
          {formatCents(cac.totalCostCents)}).
        </>
      ) : (
        <>
          {formatCents(cac.totalCostCents)} / {cac.payingCount} nowych płacących
          ={" "}
          <span className="font-semibold">
            {cac.cacCents === null ? "—" : formatCents(cac.cacCents)}
          </span>
        </>
      )}
    </div>
    <div>
      <div className="mb-1 font-semibold text-neutral-700">
        Nowi płacący ({cac.payingCount})
      </div>
      <TenantList
        rows={cac.paying.map((p) => ({
          id: p.tenantId,
          name: p.tenantName,
          right: formatDateShort(p.at),
        }))}
      />
    </div>
    <div>
      <div className="mb-1 font-semibold text-neutral-700">
        Koszty pozyskania ({formatCents(cac.totalCostCents)})
      </div>
      {cac.costs.length === 0 ? (
        <p className="text-neutral-500">Brak kosztów w okresie</p>
      ) : (
        <ul className="divide-y divide-neutral-100">
          {cac.costs.map((c) => (
            <li key={c.id} className="flex justify-between gap-3 py-1">
              <span className="truncate text-neutral-700">
                {categoryLabel(c.category)}
                {c.description ? ` · ${c.description}` : ""}
              </span>
              <span className="shrink-0 font-mono text-neutral-500">
                {formatDateShort(c.incurredAt)} · {formatCents(c.amountCents)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// buildMetricGroups — declarative 4-block layout for the MetricsTable
// ---------------------------------------------------------------------------

/** Tooltip for the "Nieprzypisane" segment — shown via InfoBadge. */
const UNASSIGNED_BADGE_TITLE =
  "Koszty bez kanału oraz płacący bez atrybucji (dane historyczne sprzed klasyfikacji inbound/outbound)";

/** Tooltip for the MRR/ARR rows (AC-11) — explains the coverage rule. */
export const MRR_COVERAGE_BADGE_TITLE =
  "MRR liczone z opłaconego okresu obowiązującego na koniec okna — klient opłacony z góry pojawi się od startu opłaconego okresu.";

/**
 * Produces three sub-rows (inbound / outbound / unassigned) for a given metric
 * from `bySegment`. Sub-rows are indented, carry no sparkline, use muted label
 * colour, and are collapsible children of `parentKey` (the headline metric row).
 * `badgeTitleFor` supplies a per-segment InfoBadge tooltip — used to explain a
 * "—" value (no cost tagged to the channel, or no paying clients).
 */
const buildSegmentSubRows = (
  keyPrefix: string,
  parentKey: string,
  data: UnitEconomicsData,
  getValue: (seg: UnitEconomicsSegment) => number | null,
  getPrev: (seg: UnitEconomicsSegment) => number | null,
  formatFn: (v: number) => string,
  badgeTitleFor: (
    seg: UnitEconomicsSegment,
    value: number | null,
  ) => string | undefined,
  invertDelta?: boolean,
) => {
  const SEGMENTS: UnitEconomicsSegment[] = ["inbound", "outbound", "unassigned"];
  return SEGMENTS.map((seg) => {
    const value = getValue(seg);
    return {
      key: `${keyPrefix}-${seg}`,
      parentKey,
      label: `· ${SEGMENT_LABELS[seg]}`,
      labelClassName:
        seg === "unassigned"
          ? "font-normal text-neutral-400 pl-8"
          : "font-normal text-neutral-500 pl-8",
      infoBadgeTitle: badgeTitleFor(seg, value),
      value,
      prevValue: getPrev(seg),
      formatFn,
      invertDelta,
    };
  });
};

/**
 * Builds the four grouped blocks (Pozyskanie / Przychód / Retencja / Wartość)
 * from the response. Drill-downs reuse the existing `MetricBreakdownPopover`
 * via each row's `breakdown` prop; the table reuses `calcDeltaPct`.
 */
export const buildMetricGroups = (data: UnitEconomicsData): MetricGroup[] => {
  const b = data.breakdown;

  // Explains a "—" CAC sub-row: a channel with payers but no tagged cost vs a
  // channel with cost but no payers (the typical legacy/untagged-costs picture).
  const cacSegmentBadge = (
    seg: UnitEconomicsSegment,
    value: number | null,
  ): string | undefined => {
    if (value !== null) {
      return seg === "unassigned" ? UNASSIGNED_BADGE_TITLE : undefined;
    }
    return data.bySegment[seg].payingClients.current === 0
      ? "Brak nowych płacących w tym kanale w wybranym okresie."
      : "Brak kosztów przypisanych do tego kanału — przypisz kanał kosztom akwizycji, aby policzyć CAC.";
  };

  // LTV:CAC and Payback are derived from CAC, so a "—" there means CAC itself was
  // not computable for the channel.
  const derivedSegmentBadge = (
    seg: UnitEconomicsSegment,
    value: number | null,
  ): string | undefined => {
    if (value !== null) {
      return seg === "unassigned" ? UNASSIGNED_BADGE_TITLE : undefined;
    }
    return "Nieobliczalne dla tego kanału — wymaga policzonego CAC (kosztów przypisanych do kanału i nowych płacących).";
  };

  return [
    {
      title: "Pozyskanie",
      rows: [
        {
          key: "trialClients",
          label: "Nowi klienci trial",
          value: data.trialClients.current,
          prevValue: data.trialClients.previous,
          formatFn: formatCount,
          breakdown: {
            title: `Nowi klienci trial (${b.trialClients.length})`,
            hasContent: b.trialClients.length > 0,
            content: (
              <TenantList
                rows={b.trialClients.map((t) => ({
                  id: t.tenantId,
                  name: t.tenantName,
                  right: formatDateShort(t.at),
                }))}
              />
            ),
          },
        },
        {
          key: "payingClients",
          label: "Nowi klienci płacący",
          value: data.payingClients.current,
          prevValue: data.payingClients.previous,
          formatFn: formatCount,
          breakdown: {
            title: `Nowi klienci płacący (${b.payingClients.length})`,
            hasContent: b.payingClients.length > 0,
            content: (
              <TenantList
                rows={b.payingClients.map((p) => ({
                  id: p.tenantId,
                  name: p.tenantName,
                  right: `${formatDateShort(p.at)} · ${formatCents(
                    p.amountCents,
                  )}`,
                }))}
              />
            ),
          },
        },
        {
          key: "conversionRatePct",
          label: "Konwersja trial → płacący",
          subtitle: "nowi płacący / nowi trial w okresie",
          value: data.conversionRatePct.current,
          prevValue: data.conversionRatePct.previous,
          formatFn: formatPct,
          trend: data.trend.conversionRatePct,
        },
        {
          key: "cacCents",
          label: "CAC",
          subtitle: "koszt pozyskania / nowy płacący",
          value: data.cacCents.current,
          prevValue: data.cacCents.previous,
          formatFn: formatCents,
          trend: data.trend.cacCents,
          invertDelta: true,
          breakdown: {
            title: "CAC — koszt pozyskania",
            hasContent: b.cac.paying.length > 0 || b.cac.costs.length > 0,
            content: <CacBreakdownContent cac={b.cac} />,
          },
        },
        // [H6] Segment sub-rows — indented, no sparkline, no drill-down,
        // collapsible under the CAC row (default collapsed).
        ...buildSegmentSubRows(
          "cac",
          "cacCents",
          data,
          (seg) => data.bySegment[seg].cacCents.current,
          (seg) => data.bySegment[seg].cacCents.previous,
          formatCents,
          cacSegmentBadge,
          true,
        ),
      ],
    },
    {
      title: "Przychód",
      rows: [
        {
          key: "mrrCents",
          label: "MRR",
          subtitle: "na koniec okresu",
          value: data.mrrCents.current,
          prevValue: data.mrrCents.previous,
          formatFn: formatCents,
          trend: data.trend.mrrCents,
          infoBadgeTitle: MRR_COVERAGE_BADGE_TITLE,
          breakdown: {
            title: `MRR — subskrypcje (miesięcznie) (${b.mrr.length})`,
            hasContent: b.mrr.length > 0,
            content: (
              <TenantList
                rows={b.mrr.map((m) => ({
                  id: m.tenantId,
                  name: m.tenantName,
                  right: formatMrrTooltipLabel(m),
                }))}
              />
            ),
          },
        },
        {
          key: "arrCents",
          label: "ARR",
          subtitle: "na koniec okresu",
          value: data.arrCents.current,
          prevValue: data.arrCents.previous,
          formatFn: formatCents,
          trend: data.trend.arrCents,
          infoBadgeTitle: MRR_COVERAGE_BADGE_TITLE,
          breakdown: {
            title: `ARR — subskrypcje (rocznie) (${b.mrr.length})`,
            hasContent: b.mrr.length > 0,
            content: (
              <TenantList
                rows={b.mrr.map((m) => ({
                  id: m.tenantId,
                  name: m.tenantName,
                  // Celowo *12, nie paidCents — możliwy dryf groszowy, patrz AC-10 / settlements.md:305
                  right: `${formatCents(m.monthlyCents * 12)} / rok`,
                }))}
              />
            ),
          },
        },
        {
          key: "arpaCents",
          label: "ARPA",
          subtitle: "MRR / aktywne płacące konta",
          value: data.arpaCents.current,
          prevValue: data.arpaCents.previous,
          formatFn: formatCents,
          trend: data.trend.arpaCents,
        },
      ],
    },
    {
      title: "Retencja",
      rows: [
        {
          key: "customerChurnRatePct",
          label: "Churn klientów",
          subtitle: "utraceni / baza płacących na początku okresu",
          value: data.customerChurnRatePct.current,
          prevValue: data.customerChurnRatePct.previous,
          formatFn: formatPct,
          trend: data.trend.customerChurnRatePct,
          invertDelta: true,
          breakdown: {
            title: `Churn klientów (${b.churnedCustomers.length})`,
            hasContent: b.churnedCustomers.length > 0,
            content: (
              <TenantList
                rows={b.churnedCustomers.map((c) => ({
                  id: c.tenantId,
                  name: c.tenantName,
                  right: `${formatCents(c.mrrAtStartCents)} / mies.`,
                }))}
              />
            ),
          },
        },
        {
          key: "grossRevenueChurnRatePct",
          label: "Churn przychodu (brutto)",
          subtitle: "utracony MRR / MRR na początku okresu",
          value: data.grossRevenueChurnRatePct.current,
          prevValue: data.grossRevenueChurnRatePct.previous,
          formatFn: formatPct,
          trend: data.trend.grossRevenueChurnRatePct,
          invertDelta: true,
          breakdown: {
            title: `Churn przychodu — utraceni klienci (${b.churnedCustomers.length})`,
            hasContent: b.churnedCustomers.length > 0,
            content: (
              <TenantList
                rows={b.churnedCustomers.map((c) => ({
                  id: c.tenantId,
                  name: c.tenantName,
                  right: `${formatCents(c.mrrAtStartCents)} / mies.`,
                }))}
              />
            ),
          },
        },
        {
          key: "nrrPct",
          label: "NRR",
          subtitle: "1 − churn przychodu brutto",
          value: data.nrrPct.current,
          prevValue: data.nrrPct.previous,
          formatFn: formatPct,
          trend: data.trend.nrrPct,
        },
      ],
    },
    {
      title: "Wartość",
      rows: [
        {
          key: "ltvPredictedCents",
          label: "LTV prognozowane",
          subtitle: "ARPA × (1 / churn) × marża",
          value: data.ltvPredictedCents.current,
          prevValue: data.ltvPredictedCents.previous,
          formatFn: formatCents,
          trend: data.trend.ltvPredictedCents,
        },
        {
          key: "ltvCacRatio",
          label: "LTV:CAC",
          value: data.ltvCacRatio.current,
          prevValue: data.ltvCacRatio.previous,
          formatFn: formatRatio,
          trend: data.trend.ltvCacRatio,
        },
        // [H6] LTV:CAC segment sub-rows (collapsible under the LTV:CAC row)
        ...buildSegmentSubRows(
          "ltvCacRatio",
          "ltvCacRatio",
          data,
          (seg) => data.bySegment[seg].ltvCacRatio.current,
          (seg) => data.bySegment[seg].ltvCacRatio.previous,
          formatRatio,
          derivedSegmentBadge,
        ),
        {
          key: "paybackMonths",
          label: "Payback",
          subtitle: "CAC / (ARPA × marża)",
          value: data.paybackMonths.current,
          prevValue: data.paybackMonths.previous,
          formatFn: formatMonths,
          trend: data.trend.paybackMonths,
          invertDelta: true,
        },
        // [H6] Payback segment sub-rows (collapsible under the Payback row)
        ...buildSegmentSubRows(
          "paybackMonths",
          "paybackMonths",
          data,
          (seg) => data.bySegment[seg].paybackMonths.current,
          (seg) => data.bySegment[seg].paybackMonths.previous,
          formatMonths,
          derivedSegmentBadge,
          true,
        ),
        {
          key: "ltvRealizedMedianCents",
          label: "LTV zrealizowane (mediana)",
          subtitle: "wszystkie płacące konta — miara kasowa",
          value: data.ltvAllMedianCents.current,
          prevValue: data.ltvAllMedianCents.previous,
          formatFn: formatCents,
          isSnapshot: true,
          breakdown: {
            title: `LTV — wszystkie konta z wpłatą (${b.ltvAll.length})`,
            hasContent: b.ltvAll.length > 0,
            content: (
              <TenantList
                rows={b.ltvAll.map((l) => ({
                  id: l.tenantId,
                  name: l.tenantName,
                  right: formatCents(l.ltvCents),
                }))}
              />
            ),
          },
        },
      ],
    },
  ];
};

export default UnitEconomicsTab;
